import { axios, APIBASE_REVIVERUSERS, APIBASE_REVIVERAPPLICATION } from "./ApiConfig";
import { useQueries, useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  Company,
  Address,
  CompanyCredit,
  CompanySpecificService,
  TeamMembers,
  CreateCompanySpecificService,
  CompanyWithTransportProperties,
  TransportCompanies,
  PartialCompanies,
  PartialCompanyStatusType,
  FileType,
  DocumentType,
  CompanyNetwork,
  AdminCompanyList,
  PartialCompanyRegistration,
  CompanyCreditRequest,
  User,
  DocumentBase64,
  CreateCompany,
  Companies,
  LockCompanyResponse,
  CreateCompanySpecificProductDto,
  CompanySpecificProduct,
  UpdateCreditLimit,
  UpdateCreditRemaining,
} from "@/types";
import { AxiosResponse } from "axios";
import { arrayBufferToBase64 } from "@/utils/arrayBufferToBase64";

export const getCompanyById = async (companyId: number): Promise<AxiosResponse<Company>> =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/get/${companyId}`);

export const useGetCompanyById = (companyId: number, options?: Partial<UseQueryOptions<Company>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getCompanyById", companyId],
    queryFn: async () => {
      const result = await getCompanyById(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getAllCompanies = async () => axios.get(`${APIBASE_REVIVERUSERS}api/company/get/all`);

export const useGetAllCompanies = (options?: Partial<UseQueryOptions<Company[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getAllCompanies"],
    queryFn: async () => {
      const result = await getAllCompanies();
      return result.data.companies;
    },
    ...queryOptions,
  });
};

export const getCompanyTeam = async (companyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/${companyId}/team`);

export const useGetCompanyTeam = (companyId: number, options?: Partial<UseQueryOptions<TeamMembers>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<TeamMembers>({
    queryKey: ["getCompanyTeam", companyId],
    queryFn: async () => {
      const result = await getCompanyTeam(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getTransportCompanies = async () =>
  axios.get<TransportCompanies>(`${APIBASE_REVIVERUSERS}api/company/get/all/transport`);

export const useGetTransportCompanies = (options?: Partial<UseQueryOptions<CompanyWithTransportProperties[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<CompanyWithTransportProperties[]>({
    queryKey: ["getTransportCompanies"],
    queryFn: async () => {
      const result = await getTransportCompanies();
      return result.data.transportCompanies;
    },
    ...queryOptions,
  });
};

export const getCompanyByIdPublic = async (companyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/get/${companyId}/public`);

export const useGetCompanyByIdPublic = (companyId: number, options?: Partial<UseQueryOptions<Company>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getCompanyByIdPublic", companyId],
    queryFn: async () => {
      const result = await getCompanyByIdPublic(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const useGetCompaniesByIdsPublic = (companies: number[], options?: any) => {
  return useQueries({
    queries: companies.map((company) => ({
      queryKey: ["getCompaniesPublic"],
      queryFn: async (): Promise<Company[]> => {
        const result = await getCompanyByIdPublic(company);
        return result.data;
      },
      enabled: !!company,
      refetchOnWindowFocus: false,
      ...options,
    })),
    combine: (results) => {
      return results
        .filter((result) => result.isSuccess && result.data && result.data)
        .flatMap((result) => result.data as Company[]);
    },
  });
};

export const getCompanyCreditById = async (companyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/${companyId}/credit`);

export const useGetCompanyCreditById = (
  companyId: number | undefined,
  options?: Partial<UseQueryOptions<CompanyCredit>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    enabled: !!companyId,
    ...options,
  };

  return useQuery({
    queryKey: ["getCompanyCreditById", companyId],
    queryFn: async () => {
      const result = await getCompanyCreditById(companyId as number);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postSaveCompanyNetwork = async (companyId: number, companies: CompanyNetwork[]) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/${companyId}/saveCompanyNetwork`, companies);

export const getCompanyNetwork = async (companyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/${companyId}/getCompanyNetwork`);

export const useGetCompanyNetwork = (companyId: number, options?: Partial<UseQueryOptions<CompanyNetwork>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery({
    queryKey: ["getCompanyNetwork", companyId],
    queryFn: async () => {
      const result = await getCompanyNetwork(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getCompanyNetworkFull = async (companyId: any) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/get/${companyId}/allForWizard`);

export const useGetCompanyNetworkFull = (companyId: number, options?: Partial<UseQueryOptions<Company[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<Company[]>({
    queryKey: ["getCompanyNetworkFull", companyId],
    queryFn: async () => {
      const result = await getCompanyNetworkFull(companyId);
      return result.data.companies;
    },
    ...queryOptions,
  });
};

export const deleteCompanySpecificProduct = async (companySpecificProductId: number) => {
  return axios.delete(`${APIBASE_REVIVERUSERS}api/company/${companySpecificProductId}/deleteCompanySpecificProduct`);
};

export const postCreateCompanySpecificProduct = async (product: CreateCompanySpecificProductDto) =>
  axios.post<number>(`${APIBASE_REVIVERUSERS}api/company/saveCompanySpecificProduct`, product);

export const updateCompanySpecificProduct = async (product: CompanySpecificProduct) =>
  axios.put(`${APIBASE_REVIVERUSERS}api/company/companySpecificProduct`, product);

export const deleteCompanySpecificProductImage = async (companySpecificProductId: number, imageIds: string[]) => {
  return axios.delete(
    `${APIBASE_REVIVERUSERS}api/company/companySpecificProduct/${companySpecificProductId}/deleteImages`,
    {
      data: imageIds,
    }
  );
};

export const deleteCompanySpecificProductAttachment = async (
  companySpecificProductId: number,
  attachmentIds: string[]
) => {
  return axios.delete<boolean>(
    `${APIBASE_REVIVERUSERS}api/company/companySpecificProduct/${companySpecificProductId}/deleteAttachments`,
    { data: attachmentIds }
  );
};

export const deleteCompanySpecificProductDocument = async (
  companySpecificProductId: number,
  documentIds: string[],
  type: "attachment" | "image"
) => {
  if (type === "attachment") {
    return await deleteCompanySpecificProductAttachment(companySpecificProductId, documentIds);
  } else {
    return await deleteCompanySpecificProductImage(companySpecificProductId, documentIds);
  }
};

export const createCompanySpecificProductImage = async (
  companySpecificProductId: number,
  documents: File[],
  fileTypes: FileType[]
) => {
  const formData = new FormData();
  documents.forEach((file) => {
    formData.append("images", file);
  });

  fileTypes.forEach((type) => {
    formData.append("fileTypes", type);
  });

  return axios.post(
    `${APIBASE_REVIVERUSERS}api/company/companySpecificProduct/${companySpecificProductId}/uploadImages`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const createCompanySpecificProductAttachments = async (
  companySpecificProductId: number,
  attachments: File[],
  fileTypes: FileType[],
  documentTypes: string[]
) => {
  const formData = new FormData();

  attachments.forEach((file) => {
    formData.append("attachments", file);
  });

  fileTypes.forEach((type) => {
    formData.append("fileTypes", type);
  });

  documentTypes.forEach((type) => {
    formData.append("documentTypes", type);
  });

  return axios.post(
    `${APIBASE_REVIVERUSERS}api/company/companySpecificProduct/${companySpecificProductId}/uploadAttachments`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const createCompanySpecificProductDocuments = async (
  companySpecificProductId: number,
  requestType: "attachment" | "image",
  documents: File[],
  fileTypes: FileType[],
  documentTypes: DocumentType[]
) => {
  if (requestType === "attachment") {
    return await createCompanySpecificProductAttachments(companySpecificProductId, documents, fileTypes, documentTypes);
  } else {
    return await createCompanySpecificProductImage(companySpecificProductId, documents, fileTypes);
  }
};

export const getCompanySpecificProducts = async (companyId: number, includeDeleted: boolean) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/${companyId}/companySpecificProducts?includeDeleted=${includeDeleted}`);

export const useGetCompanySpecificProducts = (
  companyId: number,
  includeDeleted?: boolean,
  options?: Partial<UseQueryOptions<CompanySpecificProduct[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getCompanySpecificProduct", companyId, includeDeleted ?? false],
    queryFn: async () => {
      const result = await getCompanySpecificProducts(companyId, includeDeleted ?? false);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getCompanySpecificProductById = async (companySpecificProductId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/companySpecificProduct/${companySpecificProductId}`);

export const useGetCompanySpecificProductById = (
  companySpecificProductId: number,
  options?: Partial<UseQueryOptions<CompanySpecificProduct>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<CompanySpecificProduct>({
    queryKey: ["getCompanySpecificProductById", companySpecificProductId],
    queryFn: async () => {
      const result = await getCompanySpecificProductById(companySpecificProductId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getCompanyAddresses = async (companyId: number): Promise<AxiosResponse<Address[]>> =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/${companyId}/address`);

export const useGetCompanyAddresses = (companyId: number, options?: Partial<UseQueryOptions<Address[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getCompanyAddresses", companyId],
    queryFn: async () => {
      const result = await getCompanyAddresses(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getShippingAddresses = async (companyId: number): Promise<AxiosResponse<Address[]>> =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/${companyId}/address/shipping`);

export const useGetShippingAddresses = (companyId: number, options?: Partial<UseQueryOptions<Address[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getShippingAddresses", companyId],
    queryFn: async () => {
      const result = await getShippingAddresses(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getCompanySpecificServices = async (companyId: number, includeDisabled: boolean) =>
  axios.get(
    `${APIBASE_REVIVERUSERS}api/company/companySpecificServices/${companyId}?includeDisabled=${includeDisabled}`
  );

export const useGetCompanySpecificServices = (
  companyId: number,
  includeDisabled?: boolean,
  options?: Partial<UseQueryOptions<CompanySpecificService[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CompanySpecificService[]>({
    queryKey: ["getCompanySpecificServices", companyId, includeDisabled ?? false],
    queryFn: async () => {
      const result = await getCompanySpecificServices(companyId, includeDisabled ?? false);
      return result.data;
    },
    ...queryOptions,
  });
};

export const updateCompanySpecificService = async (service: CreateCompanySpecificService, id: number) =>
  axios.put(`${APIBASE_REVIVERUSERS}api/company/companySpecificServices/${id}`, service);

export const postCreateCompanySpecificService = async (service: CreateCompanySpecificService) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/companySpecificServices`, service);

export const disableCompanySpecificService = async (id: number) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/companySpecificServices/${id}/disable`);

export const getCompanySpecificServicesPaymentIntervals = async () =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/companySpecificServices/intervals`);

export const useGetCompanySpecificServicesPaymentIntervals = (
  options?: Partial<UseQueryOptions<{ id: number; name: string }[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery({
    queryKey: ["getCompanySpecificServicesPaymentIntervals"],
    queryFn: async () => {
      const result = await getCompanySpecificServicesPaymentIntervals();
      return result.data;
    },
    ...queryOptions,
  });
};

const getCompanyLogo = async (companyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/logo/${companyId}`, {
    responseType: "arraybuffer",
  });

export const useGetCompanyLogo = (companyId: number, options?: Partial<UseQueryOptions<DocumentBase64 | null>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<DocumentBase64 | null>({
    queryKey: ["getCompanyLogo", companyId],
    queryFn: async () => {
      const response = await getCompanyLogo(companyId);
      const base64String = arrayBufferToBase64(response.data);
      const contentType = response.headers["content-type"] || "image/jpeg";
      if (response.data.byteLength === 0) {
        return null;
      }
      return {
        id: "company-logo" + companyId,
        base64: base64String,
        contentType: contentType,
        url: `data:${contentType};base64,${base64String}`,
      };
    },
    ...queryOptions,
  });
};

export const postSaveCompanyLogo = async (companyId: number, file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(`${APIBASE_REVIVERUSERS}api/company/logo/${companyId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteCompanyLogo = async (companyId: number) => {
  return axios.delete(`${APIBASE_REVIVERUSERS}api/company/logo/${companyId}`);
};

const getCompaniesToVerify = async (status?: PartialCompanyStatusType, submittedOnly?: boolean) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/registration/partial`, {
    params: { status, submittedOnly },
  });

export const useGetCompaniesToVerify = (
  status?: PartialCompanyStatusType,
  submittedOnly?: boolean,
  options?: Partial<UseQueryOptions<PartialCompanies>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getCompaniesToVerify", status, submittedOnly],
    queryFn: async () => {
      const result = await getCompaniesToVerify(status, submittedOnly);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postVerifyCompany = async (companyId: number, comment: string) =>
  axios.post(`${APIBASE_REVIVERAPPLICATION}api/admin/verifyAndCreateCompany/${companyId}`, { comment });

export const postRejectCompany = async (companyId: number, comment: string) =>
  axios.post(`${APIBASE_REVIVERAPPLICATION}api/admin/rejectCompany/${companyId}`, { comment });

const getPartialCompanyById = async (partialCompanyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/registration/getByPartialCompanyId/${partialCompanyId}`);

export const useGetPartialCompanyById = (
  partialCompanyId: number,
  options?: Partial<UseQueryOptions<PartialCompanyRegistration>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getPartialCompanyById", partialCompanyId],
    queryFn: async () => {
      const result = await getPartialCompanyById(partialCompanyId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getCompaniesForAdmin = async (status: any, limit: number, offset: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/admin/all`, {
    params: { status, limit, offset },
  });

export const useGetCompaniesForAdmin = (
  limit: number,
  offset: number,
  status?: any,
  options?: Partial<UseQueryOptions<AdminCompanyList>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery({
    queryKey: ["getCompaniesForAdmin", status, limit, offset],
    queryFn: async () => {
      const result = await getCompaniesForAdmin(status, limit, offset);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postRequestHigherCreditLimit = async (companyId: number, companyCreditRequest: CompanyCreditRequest) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/${companyId}/credit/request`, companyCreditRequest);

const getUserById = async (id: string | number) => axios.get(`${APIBASE_REVIVERUSERS}api/user/get/${id}`);

export const useGetUserById = (id: string | number, options?: Partial<UseQueryOptions<User>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery({
    queryKey: ["getUserById", id],
    queryFn: async () => {
      const result = await getUserById(id);
      return result.data;
    },
    ...queryOptions,
  });
};

export const putRejectCompanyCreditRequest = async (companyId: number, comment: string) =>
  axios.put(`${APIBASE_REVIVERUSERS}api/company/${companyId}/credit/reject`, {
    comment: comment,
    decision: "REJECTED",
  });

export const putApproveCompanyCreditRequest = async (companyId: number, comment: string) =>
  axios.put(`${APIBASE_REVIVERUSERS}api/company/${companyId}/credit/approve`, {
    comment: comment,
    decision: "APPROVED",
  });

export const postCreateDepartment = async (data: CreateCompany, parentCompanyId: number) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/${parentCompanyId}/department`, data);

const getDepartmentsByParentCompanyId = async (parentCompanyId: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/${parentCompanyId}/departments`);

export const useGetDepartmentsByParentCompanyId = (
  parentCompanyId: number,
  options?: Partial<UseQueryOptions<Companies>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Companies>({
    queryKey: ["getDepartmentsByParentCompanyId", parentCompanyId],
    queryFn: async () => {
      const result = await getDepartmentsByParentCompanyId(parentCompanyId);
      return result.data;
    },
    ...queryOptions,
  });
};
export const putLockCompany = async (companyId: number) =>
  axios.put<LockCompanyResponse>(`${APIBASE_REVIVERUSERS}api/company/lock/${companyId}`);

export const putUnlockCompany = async (companyId: number) =>
  axios.put<LockCompanyResponse>(`${APIBASE_REVIVERUSERS}api/company/unlock/${companyId}`);

const getCompanyCreditRequest = async (companyId: number) =>
  axios.get<CompanyCredit>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/credit/request`);

export const useGetCompanyCreditRequest = (companyId: number, options?: Partial<UseQueryOptions<CompanyCredit>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CompanyCredit>({
    queryKey: ["getCompanyCreditRequest", companyId],
    queryFn: async () => {
      const result = await getCompanyCreditRequest(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const putUpdateCreditLimit = async (updateCreditLimit: UpdateCreditLimit) =>
  axios.put(`${APIBASE_REVIVERUSERS}api/company/updateCreditLimit`, updateCreditLimit);

const getCompanyCreditHistory = async (companyId: number) =>
  axios.get<CompanyCredit[]>(`${APIBASE_REVIVERUSERS}api/company/${companyId}/credit/history`);

export const useGetCompanyCreditHistory = (companyId: number, options?: Partial<UseQueryOptions<CompanyCredit[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    enabled: !!companyId,
    ...options,
  };

  return useQuery<CompanyCredit[]>({
    queryKey: ["getCompanyCreditHistory", companyId],
    queryFn: async () => {
      const result = await getCompanyCreditHistory(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};
